import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Loader2, ExternalLink, Check } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useUserWclGuilds } from 'app/frontend/base/config/queries'
import Card from 'app/frontend/base/components/Card'
import { getErrorDetails } from 'app/frontend/base/helpers'
import { useTranslation } from 'react-i18next'

type WclGuild = {
  id: number
  name: string
}

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  onLink: (guild: WclGuild) => void
  isLinking: boolean
}

export const WclIconLink = ({ id }: { id: number }) => {
  return (
    <a
      href={`https://www.warcraftlogs.com/guild/reports-list/${id}`}
      target='_blank'
      rel='noreferrer'
      className='text-sm text-muted-foreground hover:text-muted-foreground/90 inline-flex items-center gap-1'
    >
      <ExternalLink size={14} />
    </a>
  )
}

const WclGuildLinkDialog = ({
  open,
  onOpenChange,
  onLink,
  isLinking,
}: Props) => {
  const [selectedGuildId, setSelectedGuildId] = useState<number | null>(null)
  const { t } = useTranslation()
  const { data, isLoading, isError, error } = useUserWclGuilds(open)
  const guilds = data?.guilds ?? []
  const selectedGuild = useMemo(
    () => guilds.find((guild) => guild.id === selectedGuildId),
    [guilds, selectedGuildId],
  )

  useEffect(() => {
    if (!open) {
      setSelectedGuildId(null)
    }
  }, [open])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-2xl'>
        <DialogHeader>
          <DialogTitle>Link Warcraft Logs guild</DialogTitle>
          <DialogDescription>
            Choose one of the guilds your Warcraft Logs account has access to.
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-2'>
          {isLoading ? (
            <div className='py-2 flex justify-center'>
              <Loader2 className='animate-spin' />
            </div>
          ) : null}

          {isError ? (
            <div className='py-3 text-sm text-destructive-foreground'>
              {getErrorDetails(error as Error, t).description ??
                'Could not load guilds. Please make sure your Warcraft Logs account is linked and try again.'}
            </div>
          ) : null}

          {!isLoading && !isError && guilds.length === 0 ? (
            <div className='py-3 text-sm text-muted-foreground'>
              No guilds were found for this Warcraft Logs account.
            </div>
          ) : null}

          {!isLoading && !isError
            ? guilds.map((guild) => {
                const selected = selectedGuildId === guild.id
                return (
                  <Card
                    key={guild.id}
                    className={`p-3 flex items-center justify-between gap-3 ${
                      selected ? 'border-primary' : ''
                    }`}
                  >
                    <div className='min-w-0 flex items-center gap-3'>
                      <div className='font-medium text-lg truncate'>
                        {guild.name}
                      </div>
                      <WclIconLink id={guild.id} />
                    </div>

                    <Button
                      type='button'
                      variant={selected ? 'default' : 'outline'}
                      size='sm'
                      onClick={() => setSelectedGuildId(guild.id)}
                      className='w-24'
                    >
                      {selected ? (
                        <span className='inline-flex items-center gap-1.5'>
                          <Check size={14} />
                          Chosen
                        </span>
                      ) : (
                        'Choose'
                      )}
                    </Button>
                  </Card>
                )
              })
            : null}
        </div>

        <DialogFooter>
          <Button
            type='button'
            variant='outline'
            onClick={() => onOpenChange(false)}
            disabled={isLinking}
          >
            Cancel
          </Button>
          <Button
            type='button'
            disabled={!selectedGuild || isLinking}
            loading={isLinking}
            onClick={() => {
              if (selectedGuild) onLink(selectedGuild)
            }}
          >
            Link
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default WclGuildLinkDialog
